import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "@majourneys/icons";
interface TripStopNoteEditorProps {
  readonly note: string | undefined;
  readonly onSave: (note: string | undefined) => void;
  /**
   * Controlled open state, for when the stop's ⋯ menu opens the editor. With
   * `hideTrigger` the sticky-note button is not drawn: the menu is the way in.
   */
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
  readonly hideTrigger?: boolean;
}

/**
 * Inline note editor: a sticky-note button (highlighted when a note exists)
 * that opens a small popover with a textarea. Replaces window.prompt() so the
 * note UI is styled, localized, RTL-aware, focus-managed and screen-reader
 * operable. Enter/Save commits, Escape / outside-tap cancels, Clear removes.
 */
export function TripStopNoteEditor({
  note,
  onSave,
  open: openProp,
  onOpenChange,
  hideTrigger = false,
}: TripStopNoteEditorProps) {
  const { t } = useTranslation();
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (next: boolean | ((o: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(open) : next;
    if (openProp === undefined) setOpenState(value);
    onOpenChange?.(value);
  };
  const [draft, setDraft] = useState(note ?? "");
  const wrapRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    setDraft(note ?? "");
    const id = requestAnimationFrame(() => textareaRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open, note]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const commit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = draft.trim();
    onSave(trimmed.length > 0 ? trimmed : undefined);
    setOpen(false);
  };

  const clear = () => {
    onSave(undefined);
    setOpen(false);
  };

  return (
    <div className="mj-trip-note-editor" ref={wrapRef}>
      {!hideTrigger && (
        <button
          type="button"
          className={`mj-trip-timeline__edit-btn${note ? " is-on" : ""}`}
          aria-label={t("trip.timeline.note")}
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <Icon name="ri-sticky-note-line" aria-hidden="true" />
        </button>
      )}
      {open && (
        <form className="mj-trip-note-editor__popover" onSubmit={commit}>
          <div className="mj-trip-note-editor__header">
            <Icon name="ri-sticky-note-line" aria-hidden="true" />
            <span>{t("trip.timeline.note")}</span>
          </div>
          <textarea
            ref={textareaRef}
            className="mj-trip-note-editor__input"
            rows={3}
            maxLength={400}
            value={draft}
            placeholder={t("trip.timeline.note_prompt")}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
            }}
          />
          <div className="mj-trip-note-editor__footer">
            {note && (
              <button
                type="button"
                className="mj-trip-note-editor__clear"
                onClick={clear}
              >
                {t("trip.time.reset")}
              </button>
            )}
            <button
              type="button"
              className="mj-trip-note-editor__cancel"
              onClick={() => setOpen(false)}
            >
              {t("trip.cancel")}
            </button>
            <button type="submit" className="mj-trip-note-editor__save">
              <Icon name="ri-check-line" aria-hidden="true" />
              {t("trip.time.save")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
